import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CONTENT_TYPES } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Loader, CheckCircle2, RotateCcw, Video, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { task: "Task", content: "Content", ...Object.fromEntries(CONTENT_TYPES.map((t) => [t.v, t.l])) };
const VIDEO = new Set(["reel", "video"]);

export function InProgressNow({ types = null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["pending-work"], queryFn: async () => (await api.get("/pending-work")).data });

  const refresh = () => {
    ["pending-work", "my-day", "ws-my-clients", "pool-design", "pool-video", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const act = async (r, next, msg) => {
    const url = r.entity_type === "task" ? `/tasks/${r.id}` : `/content/${r.id}`;
    await api.put(url, { status: next });
    toast.success(msg);
    refresh();
  };

  if (isLoading || !data)
    return <Card className="flex h-24 items-center justify-center rounded-lg border-zinc-200"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></Card>;

  const rows = (data.rows || [])
    .filter((r) => r.raw_status === "in_progress" && (!types || types.includes(r.work_type)))
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));

  const isBoss = user?.role === "super_admin" || user?.role === "manager";
  const mine = (r) => isBoss || (r.entity_type === "task" ? r.owner_id === user?.id : r.started_by_id === user?.id);
  const scopeWord = types ? (types.includes("reel") ? "Videos" : "Designs") : "Videos & posts";

  return (
    <Card className="overflow-hidden rounded-lg border-orange-200 shadow-sm" data-testid="in-progress-now">
      <div className="flex items-center gap-2 border-b border-orange-100 bg-orange-50/60 px-5 py-4">
        <Loader className="h-4 w-4 animate-spin text-orange-500" style={{ animationDuration: "3s" }} />
        <h3 className="font-heading text-base font-semibold">In Progress Now</h3>
        <Badge color="orange" data-testid="inprogress-count">{rows.length}</Badge>
        <span className="ml-1 text-xs text-zinc-400">{scopeWord} being worked on {data.is_manager ? "across the agency" : "right now"}</span>
      </div>

      {rows.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-400">Nothing in progress right now — start a task or pick work from the pool.</div>
      ) : (
        <div className="grid grid-cols-1 gap-2 p-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((r) => (
            <div key={`${r.entity_type}-${r.id}`} className="rounded-lg border border-zinc-100 p-3 transition-colors hover:border-zinc-200" data-testid={`ip-row-${r.id}`}>
              <div className="flex items-start gap-2.5">
                <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${VIDEO.has(r.work_type) ? "bg-rose-100 text-rose-600" : "bg-blue-100 text-blue-600"}`}>
                  {VIDEO.has(r.work_type) ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <button onClick={() => r.client_id && navigate(`/clients/${r.client_id}`)} className="block w-full truncate text-left text-sm font-medium hover:underline">{r.title}</button>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-zinc-500">
                    <span className="font-medium text-zinc-600">{r.client_name}</span>
                    <span>· {TYPE_LABEL[r.work_type] || r.work_type}</span>
                    <span>· {r.date ? fmtDate(r.date) : "—"}</span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-400">Working: <span className="font-medium text-zinc-600">{r.owner || "—"}</span></div>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-end gap-1.5">
                {mine(r) ? (
                  <>
                    <Button size="sm" variant="ghost" className="px-2 text-zinc-400 hover:text-zinc-700" title="Undo — back to pending" onClick={() => act(r, r.entity_type === "task" ? "todo" : "planned", "Reverted to pending")} data-testid={`ip-undo-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="outline" onClick={() => act(r, r.entity_type === "task" ? "completed" : "ready_for_approval", r.entity_type === "task" ? "Task completed" : "Marked ready for approval")} data-testid={`ip-complete-${r.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Mark Completed</Button>
                  </>
                ) : (
                  <span className="text-xs italic text-zinc-400" data-testid={`ip-locked-${r.id}`}>Only {r.owner || "the owner"} can complete</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
